/**
 * Trace Node Segmenter
 *
 * Groups Playwright trace actions into per-workflow-node windows using the
 * named `tracing.group()` spans the executor opens around each step:
 *
 * - `Step N: <label>`            — agent-executed node (the harvest target)
 * - `Cache Replay N: <name>`     — replayed node. Still harvestable: L2 gap
 *   healing and recovery actions are recorded INSIDE this group, so a step
 *   that replayed with `l2_gap`/`recovery` healing carries its live agent
 *   work here, not in a `Step N:` group. (Legacy traces omit the number.)
 * - `Canary Internal: <op>`      — plumbing (snapshot capture, element resolution)
 *
 * Tolerant by design: failure-path traces leave the failing step's group
 * unclosed (no `after` event), so an open-ended group extends to the next
 * top-level group's start (or the end of the trace). Actions are assigned by
 * `parentId` containment when present, with a start-time window fallback.
 *
 * @module optimizer/trace-node-segmenter
 */

import type { TraceAction, TraceGroup } from '@chatsdet/trace-tools';

export type NodeSegmentKind = 'agent-step' | 'cache-replay';

export interface NodeSegment {
  kind: NodeSegmentKind;
  /** 1-based step number parsed from the `Step N:` / `Cache Replay N:` group name. */
  stepNumber?: number;
  /** Human label from the group name (node label or replayed node name). */
  label: string;
  /** The group's callId — actions inside carry it (transitively) as parentId. */
  groupCallId: string;
  startTime: number;
  wallTime?: number;
  /** Open-ended (failure path) segments extend to the next segment's start. */
  endTime?: number;
  /** Wall-clock equivalent of `endTime` for open-ended cross-process windows. */
  endWallTime?: number;
  /**
   * Trace actions inside this segment, with their index into the full
   * `parseTraceActions` array (the index `reconstructSnapshots` expects).
   */
  actions: Array<{ index: number; action: TraceAction }>;
}

const STEP_GROUP = /^Step (\d+): (.*)$/;
// The step number is optional: traces recorded before the replay handler
// numbered its groups carry the bare `Cache Replay: <name>` form.
const CACHE_REPLAY_GROUP = /^Cache Replay(?: (\d+))?: (.*)$/;
const INTERNAL_GROUP_PREFIX = 'Canary Internal:';

/**
 * Segment a run's trace actions into per-node windows.
 *
 * @param groups - from `parseTraceGroups(traceZipPath)`
 * @param actions - from `parseTraceActions(traceZipPath)` (indices must match)
 */
export function segmentTraceByNode(groups: TraceGroup[], actions: TraceAction[]): NodeSegment[] {
  // Top-level node groups only — internal groups nest inside step groups and
  // are excluded; their actions still belong to the enclosing step.
  const nodeGroups = groups.filter(
    (g) =>
      !g.name.startsWith(INTERNAL_GROUP_PREFIX) &&
      (STEP_GROUP.test(g.name) || CACHE_REPLAY_GROUP.test(g.name))
  );

  // Close open-ended groups at the next node group's start so a failure-path
  // trace (unclosed final group) still yields a usable window.
  const sortGroupsByWallTime = nodeGroups.every((g) => typeof g.wallTime === 'number');
  const sorted = [...nodeGroups].sort((a, b) =>
    sortGroupsByWallTime ? (a.wallTime ?? 0) - (b.wallTime ?? 0) : a.startTime - b.startTime
  );
  const segments: NodeSegment[] = sorted.map((g, idx) => {
    const stepMatch = STEP_GROUP.exec(g.name);
    const replayMatch = CACHE_REPLAY_GROUP.exec(g.name);
    const effectiveEnd = g.endTime ?? sorted[idx + 1]?.startTime;
    const effectiveEndWallTime = g.endTime === undefined ? sorted[idx + 1]?.wallTime : undefined;
    const stepNumberRaw = stepMatch ? stepMatch[1] : replayMatch?.[1];
    return {
      kind: stepMatch ? ('agent-step' as const) : ('cache-replay' as const),
      stepNumber: stepNumberRaw !== undefined ? Number(stepNumberRaw) : undefined,
      label: stepMatch ? stepMatch[2]! : replayMatch![2]!,
      groupCallId: g.callId,
      startTime: g.startTime,
      ...(g.wallTime !== undefined ? { wallTime: g.wallTime } : {}),
      endTime: effectiveEnd,
      ...(effectiveEndWallTime !== undefined ? { endWallTime: effectiveEndWallTime } : {}),
      actions: [],
    };
  });

  if (segments.length === 0) return segments;

  // parentId chain: group callId -> enclosing node-group callId (internal
  // groups resolve through their parents).
  const groupById = new Map(groups.map((g) => [g.callId, g]));
  const nodeGroupIds = new Set(segments.map((s) => s.groupCallId));
  const resolveNodeGroup = (parentId: string | undefined): string | undefined => {
    let current = parentId;
    // Bounded walk — trace group nesting is shallow; guard against cycles.
    for (let hops = 0; current && hops < 16; hops++) {
      if (nodeGroupIds.has(current)) return current;
      current = groupById.get(current)?.parentId;
    }
    return undefined;
  };

  const segmentByGroupId = new Map(segments.map((s) => [s.groupCallId, s]));
  for (let i = 0; i < actions.length; i++) {
    const action = actions[i]!;
    const byParent = resolveNodeGroup(action.parentId);
    if (byParent) {
      segmentByGroupId.get(byParent)!.actions.push({ index: i, action });
      continue;
    }
    // Fallback: start-time window (covers traces recorded before parentId
    // capture, and actions Playwright didn't parent into the group).
    const within = segments.find((s) => actionFallsWithinSegment(action, s));
    within?.actions.push({ index: i, action });
  }

  return segments;
}

function actionFallsWithinSegment(action: TraceAction, segment: NodeSegment): boolean {
  if (
    typeof action.wallTime === 'number' &&
    typeof segment.wallTime === 'number' &&
    (segment.endWallTime !== undefined || segment.endTime === undefined)
  ) {
    return (
      action.wallTime >= segment.wallTime &&
      (segment.endWallTime === undefined || action.wallTime < segment.endWallTime)
    );
  }
  return action.startTime >= segment.startTime && (segment.endTime === undefined || action.startTime < segment.endTime);
}

/** One run step, in `orderIndex` order, for segment→node resolution. */
export interface SegmentResolutionStep {
  nodeId: string | null;
  /** Display label as the executor derives it (the same string the trace group title used). */
  label: string;
  /** Alternate exact labels known to have been used by trace groups for this step. */
  traceLabels?: readonly string[];
  /** Seed/setup steps belong to a different workflow's trace context. */
  isSeedStep?: boolean;
}

/**
 * Resolve each segment to its workflow node id, in segment order.
 *
 * Numbered segments (`Step N:` / `Cache Replay N:`) resolve positionally via
 * `steps[N-1]` only when the label also matches. Some long runs upload several
 * Playwright trace segments; after a browser restart the group numbering can be
 * local to that trace segment rather than global to the workflow run. In that
 * case the numbered group label will not match `steps[N-1]`, so fall back to
 * forward label matching. Legacy un-numbered cache-replay segments use the same
 * label path, consuming steps forward-only so repeated labels map to
 * successive steps and a renamed/unknown label resolves to `null` rather than a
 * wrong node.
 */
export function resolveSegmentNodeIds(
  segments: ReadonlyArray<Pick<NodeSegment, 'kind' | 'stepNumber' | 'label' | 'wallTime'>>,
  steps: ReadonlyArray<SegmentResolutionStep>
): Array<string | null> {
  const orderedSegments = orderSegmentsForResolution(segments);
  const chunks = splitNumberingChunks(orderedSegments);
  const resolved: Array<string | null> = Array.from({ length: segments.length }, () => null);
  const consumed = new Set<number>();

  const labelMatches = (step: SegmentResolutionStep, label: string): boolean =>
    step.label === label || (step.traceLabels?.includes(label) ?? false);

  const resolveStep = (idx: number): string | null => {
    consumed.add(idx);
    const step = steps[idx]!;
    return step.isSeedStep ? 'seed' : step.nodeId;
  };

  for (const chunk of chunks) {
    let cursor = 0;
    const findForwardLabelMatch = (label: string): number | null => {
      for (let j = cursor; j < steps.length; j++) {
        if (consumed.has(j)) continue;
        if (labelMatches(steps[j]!, label)) return j;
      }
      return null;
    };

    for (let i = chunk.start; i < chunk.end; i++) {
      const entry = orderedSegments[i]!;
      const segment = entry.segment;
      if (segment.stepNumber !== undefined) {
        const idx = segment.stepNumber - 1;
        const numberedStep = steps[idx];
        if (idx >= cursor && !consumed.has(idx) && numberedStep && labelMatches(numberedStep, segment.label)) {
          resolved[entry.originalIndex] = resolveStep(idx);
          cursor = idx + 1;
          continue;
        }
      }
      const labelMatch = findForwardLabelMatch(segment.label);
      if (labelMatch !== null) {
        resolved[entry.originalIndex] = resolveStep(labelMatch);
        cursor = labelMatch + 1;
      }
    }
  }

  return resolved;
}

type SegmentResolutionEntry = {
  originalIndex: number;
  segment: Pick<NodeSegment, 'kind' | 'stepNumber' | 'label' | 'wallTime'>;
};

function orderSegmentsForResolution(
  segments: ReadonlyArray<Pick<NodeSegment, 'kind' | 'stepNumber' | 'label' | 'wallTime'>>
): SegmentResolutionEntry[] {
  const entries = segments.map((segment, originalIndex) => ({ originalIndex, segment }));
  if (!segments.every((segment) => typeof segment.wallTime === 'number')) return entries;
  return [...entries].sort((a, b) => (a.segment.wallTime ?? 0) - (b.segment.wallTime ?? 0));
}

function splitNumberingChunks(
  segments: ReadonlyArray<SegmentResolutionEntry>
): Array<{ start: number; end: number }> {
  if (segments.length === 0) return [];
  const chunks: Array<{ start: number; end: number }> = [];
  let start = 0;
  let previousNumber: number | undefined;
  for (let i = 0; i < segments.length; i++) {
    const currentNumber = segments[i]!.segment.stepNumber;
    if (
      i > start &&
      currentNumber !== undefined &&
      previousNumber !== undefined &&
      currentNumber <= previousNumber
    ) {
      chunks.push({ start, end: i });
      start = i;
    }
    if (currentNumber !== undefined) previousNumber = currentNumber;
  }
  chunks.push({ start, end: segments.length });
  return chunks;
}
